import { ChangeDetectorRef, Component, HostListener, OnDestroy } from '@angular/core';
import { ICellEditorParams } from 'ag-grid-community';
import { BehaviorSubject, Subject } from 'rxjs';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { FeedbackService } from '../../../core/feedback.service';
import { ENTER, TAB } from '@angular/cdk/keycodes';

/**
 * Do not instantiate but extend this class to create custom cell editors.
 * Full documentation: https://ag-grid.com/angular-data-grid/cell-editors/#popup-editor
 */
@Component({
  template: '',
})
export abstract class AgEditorComponent<T> implements ICellEditorAngularComp, OnDestroy {
  // Prevent event propagation (we want to use TAB to shift focus within the
  // editor component) which would normally open next editable cell's editor.
  // Also prevent saving on 'shift' + 'ENTER' which is used to add a new line
  // (ENTER would cause a save)
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.keyCode === TAB || (event.shiftKey && event.keyCode === ENTER)) {
      event.stopPropagation();
    }
  }

  private readonly params: BehaviorSubject<ICellEditorParams<T>> =
      new BehaviorSubject<ICellEditorParams<T>>(null);
  protected editorFormGroup: FormGroup;

  protected readonly params$ = this.params.asObservable();

  protected componentDestroyed = new Subject<void>();

  protected constructor(protected feedbackService: FeedbackService,
                        protected cd: ChangeDetectorRef) {}

  /**
   * Params for rendering this component.
   * @param params
   * @private
   */
  agInit(params: ICellEditorParams<T>): void {
    this.params.next(params);
    this.editorFormGroup = this.createEditorFormGroup(params);
    this.cd.markForCheck(); // should fix BLS-6200

    this.editorFormGroup.valueChanges
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => {
          // makes sure errors show up if there are any
          this.editorFormGroup.markAllAsTouched();
        })
  }

  /**
   * Gets called with the latest cell editor params every time they update
   * @param params
   * @private
   */
  refresh(params: ICellEditorParams<T>): void {
    this.params.next(params);
  }

  /**
   * Return the final value - called by the grid once after editing is complete
   * @private
   */
  getValue() {
    return this.getNewFieldValue();
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /**
   * Gets called once after initialised. If you return true, the editor will
   * appear in a popup, so is not constrained to the boundaries of the cell.
   */
  isPopup(): boolean {
    return true;
  }

  /**
   * Gets called once, only if isPopup() returns true. Return "over" if the popup
   * should cover the cell, or "under" if it should be positioned below leaving the
   * cell value visible.
   */
  getPopupPosition(): 'over' | 'under' {
    return 'over';
  }

  /**
   * Gets called once after editing is complete. If your return true, then the new value will not
   * be used. The editing will have no impact on the record. Use this if you do not want a new
   * value from your gui, i.e. you want to cancel the editing.
   */
  isCancelAfterEnd() {
    if (this.editorFormGroup.invalid) {
      this.feedbackService.showWarningSnackbar({
        data: { userFriendlyMessage: 'Changes were not saved because the form had errors!' }, duration: 3000,
      })
    }

    return this.editorFormGroup.invalid;
  }

  /**
   * Return the new location field value
   * Override in child component if needed
   * @protected
   */
  protected getNewFieldValue(): any {
    return this.editorFormGroup.get(this.params?.value?.colDef?.field)?.value;
  };

  /**
   * Return a new FormGroup instance
   * Override in child component if needed
   * @protected
   */
  protected createEditorFormGroup(params: ICellEditorParams<T>): UntypedFormGroup {
    return new FormGroup({
      [params?.colDef?.field]: new FormControl(params.value, { nonNullable: true }),
    });
  };

}